import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { namespace } from '@common/config/constants';
import { 
  FileText, 
  Loader2,
  Settings,
  Sliders
} from "lucide-react";
import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { KnowledgeBase } from '@/common/models/knowledge-base';
import { DBKnowledgeBaseThunks, knowledgeBaseSelectByQuery } from '@/common/reducers/DBServiceThunks';
import { useTranslation } from 'react-i18next';
import { ToolSettingsComponentProps } from './tool-settings-dialog';

export interface KnowledgeBaseSettingsInterface {
  knowledgeBaseIds?: string[];
  maxResults?: number;
  similarityThreshold?: number;
}

const KnowledgeBaseSettings: React.FC<ToolSettingsComponentProps<KnowledgeBaseSettingsInterface>> = ({
  initialSettings,
  onSaveSettings
}) => {
  const { t, i18n } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState('general');
  const isRTL = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';
  
  // Initialize internal settings with defaults or initialSettings
  const [internalSettings, setInternalSettings] = useState<KnowledgeBaseSettingsInterface>({
    knowledgeBaseIds: initialSettings?.knowledgeBaseIds || [],
    maxResults: initialSettings?.maxResults || 5,
    similarityThreshold: initialSettings?.similarityThreshold || 0.7
  });

  // Create derived state for easier access and to ensure we're using internal state
  const knowledgeBaseIds = internalSettings.knowledgeBaseIds || [];
  const maxResults = internalSettings.maxResults;
  const similarityThreshold = internalSettings.similarityThreshold;
  
  // Get knowledge bases
  const knowledgeBases = useAppSelector(state => 
    knowledgeBaseSelectByQuery(state.DBKnowledgeBaseReducer, {
      status: 'active'
    })
  );
  
  // Get loading state
  const isLoadingKnowledgeBases = useAppSelector(state =>
    state.DBKnowledgeBaseReducer?.itemsLoading ?? false
  );
  
  // Fetch knowledge bases on mount
  useEffect(() => {
    dispatch(DBKnowledgeBaseThunks.find({}));
  }, [dispatch]);

  // Toggle knowledge base selection
  const toggleKnowledgeBase = (id: string) => {
    console.log('Toggling knowledge base:', id);

    setInternalSettings((previousSettings) => {
      const previousIds = previousSettings.knowledgeBaseIds || [];
      const selectedIds = previousIds.includes(id)
        ? previousIds.filter((kbId) => kbId !== id)
        : [...previousIds, id];

      const updatedSettings = {
        ...previousSettings,
        knowledgeBaseIds: selectedIds
      };

      console.log('Previous knowledgeBaseIds:', knowledgeBaseIds);
      console.log('New knowledgeBaseIds:', selectedIds);

      if (onSaveSettings) {
        console.log('Calling onSaveSettings after toggle with:', updatedSettings);
        onSaveSettings(updatedSettings);
      }

      return updatedSettings;
    });
  };

  // Listen for save event from parent
  useEffect(() => {
    const handleSaveEvent = () => {
      if (onSaveSettings) {
        console.log('KnowledgeBaseSettings: Save event triggered');
        console.log('KnowledgeBaseSettings: Current state:', internalSettings);
        onSaveSettings({
          ...internalSettings,
          knowledgeBaseIds: [...(internalSettings.knowledgeBaseIds || [])] // Create a new array to ensure it's not mutated
        });
      } else {
        console.warn('KnowledgeBaseSettings: Save event triggered but onSaveSettings is not available');
      }
    };

    console.log('KnowledgeBaseSettings: Adding save-tool-settings event listener');
    document.addEventListener('save-tool-settings', handleSaveEvent);
    
    return () => {
      console.log('KnowledgeBaseSettings: Removing save-tool-settings event listener');
      document.removeEventListener('save-tool-settings', handleSaveEvent);
    };
  }, [internalSettings, onSaveSettings]);

  // Debug log when knowledgeBaseIds changes
  useEffect(() => {
    console.log('Current knowledgeBaseIds state:', knowledgeBaseIds);
  }, [knowledgeBaseIds]);

  return (
    <div className="flex flex-col h-full" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Tabs - Always visible */}
      <div className="flex border-b border-[var(--color-border)] sticky top-0 bg-[var(--color-surface)] z-10">
        <button 
          className={`px-4 py-2 transition-colors ${activeTab === 'general' ? 'text-primary border-b-2 border-primary font-medium' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}
          onClick={() => setActiveTab('general')}
        >
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {t('chatbot.toolSettings.knowledgeBase.tabs.general')}
          </div>
        </button>
        <button 
          className={`px-4 py-2 transition-colors ${activeTab === 'advanced' ? 'text-primary border-b-2 border-primary font-medium' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'}`}
          onClick={() => setActiveTab('advanced')}
        >
          <div className="flex items-center gap-2">
            <Sliders className="h-4 w-4" />
            {t('chatbot.toolSettings.knowledgeBase.tabs.advanced')}
          </div>
        </button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-grow overflow-y-auto p-6 max-h-[60vh]">
        {activeTab === 'general' && (
          <div className="space-y-4 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)]">
            <div className="p-4">
              <h3 className="text-base font-medium my-2 text-[var(--color-text)]">
                {t('chatbot.toolSettings.knowledgeBase.sections.knowledgeBasesTitle')}
              </h3>
              <p className="text-sm text-[var(--color-text-muted)] mb-3">
                {t('chatbot.toolSettings.knowledgeBase.sections.selectKnowledgeBasesDescription')}
              </p>
              
              <div className="space-y-3">
                {isLoadingKnowledgeBases ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-6 w-6 animate-spin text-[var(--color-text-muted)]" />
                  </div>
                ) : knowledgeBases.length === 0 ? (
                  <p className="text-sm text-[var(--color-text-muted)] text-center py-4">
                    {t('chatbot.toolSettings.knowledgeBase.emptyState')}
                  </p>
                ) : knowledgeBases.map((kb: KnowledgeBase) => (
                  <div
                    key={kb._id}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleKnowledgeBase(kb._id ?? '')
                    }}
                    className={`p-3 rounded-md border cursor-pointer transition-colors ${
                      knowledgeBaseIds.includes(kb._id ?? '') 
                        ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:border-emerald-800 dark:hover:bg-emerald-900/30'
                        : 'bg-[var(--color-surface)] border-[var(--color-border)] hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex gap-3">
                        <div className={`p-1 rounded ${knowledgeBaseIds.includes(kb._id ?? '') ? 'bg-emerald-100 dark:bg-emerald-900/50' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          <FileText className={`h-4 w-4 ${knowledgeBaseIds.includes(kb._id ?? '') ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[var(--color-text)]">{kb.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${kb.status === 'active' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400' : 'bg-slate-50 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}>
                              {t(`chatbot.toolSettings.knowledgeBase.status.${kb.status}`, { defaultValue: kb.status })}
                            </span>
                          </div>
                          <p className="text-xs text-[var(--color-text-muted)]">
                            {kb.description || t('knowledgeBase.defaultDescription')}
                          </p>
                          <p className="text-xs text-[var(--color-text-muted)]">
                            {t('chatbot.toolSettings.knowledgeBase.documentsCount', { count: kb.documentCount || 0 })}
                          </p>
                        </div>
                      </div>
                      <Switch 
                        checked={knowledgeBaseIds.includes(kb._id ?? '')} 
                        onCheckedChange={() => toggleKnowledgeBase(kb._id ?? '')}
                        onClick={(e) => e.stopPropagation()}
                        className="[direction:ltr]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'advanced' && (
          <div className="space-y-4 p-4 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)]">
            <div>
              <Label htmlFor="maxResults">{t('chatbot.toolSettings.knowledgeBase.fields.maxResults')}</Label>
              <Input
                id="maxResults"
                type="number"
                value={maxResults}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value, 10);
                  if (Number.isNaN(newValue)) {
                    return;
                  }
                  const updatedSettings = {
                    ...internalSettings,
                    maxResults: newValue
                  };
                  
                  setInternalSettings(updatedSettings);
                  
                  // Call onSaveSettings directly after state update
                  if (onSaveSettings) {
                    setTimeout(() => {
                      onSaveSettings(updatedSettings);
                    }, 0);
                  }
                }}
                className="mt-1"
                min={1}
                max={20}
              />
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {t('chatbot.toolSettings.knowledgeBase.sections.maxResultsDescription')}
              </p>
            </div>
            
            <div>
              <Label htmlFor="similarityThreshold">{t('chatbot.toolSettings.knowledgeBase.fields.similarityThreshold')}</Label>
              <Input
                id="similarityThreshold"
                type="number"
                step={0.1}
                min={0}
                max={1}
                value={similarityThreshold}
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value);
                  if (Number.isNaN(newValue)) {
                    return;
                  }
                  const updatedSettings = {
                    ...internalSettings,
                    similarityThreshold: newValue
                  };
                  
                  setInternalSettings(updatedSettings);
                  
                  // Call onSaveSettings directly after state update
                  if (onSaveSettings) {
                    setTimeout(() => {
                      onSaveSettings(updatedSettings);
                    }, 0);
                  }
                }}
                className="mt-1"
              />
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                {t('chatbot.toolSettings.knowledgeBase.sections.similarityThresholdDescription')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBaseSettings; 
